/**
 * PaytypePicker - Wrapper around generic EntityPicker
 * 
 * Provides paytype-specific configuration and maintains backward compatibility
 * with any code that imports PaytypePickerField or PaytypePickerModal
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';

// ── Paytype-specific configuration ────────────────────────────────────────
export const PAYTYPE_CONFIG = {
	apiEndpoint: "/api/Paytype/active",
	entityName: "Paytype",
	idField: "id",
	displayFields: {
		primary: "description",
	},
	tableHeaders: ["Description"],
	tableAccessors: ["description"],
	searchFields: [
		{ label: "All", field: "all", index: 0 },
		{ label: "Description", field: "description", index: 1 },
	],
	placeholder: "--Select paytype--"
};

// ── PaytypePickerField wrapper ────────────────────────────────────────────
/**
 * PaytypePickerField
 * 
 * Usage:
 * <PaytypePickerField
 *   label="Paytype"
 *   payTypeId={form.payTypeId}
 *   displayValue={form.payTypeDescription}
 *   onSelect={(paytype) => {
 *     set("payTypeId", paytype.id);
 *     set("payTypeCode", paytype.code);
 *     set("payTypeDescription", paytype.description);
 *   }}
 *   onClear={() => {
 *     set("payTypeId", "");
 *     set("payTypeCode", "");
 *     set("payTypeDescription", "");
 *   }}
 *   disabled={!editable}
 * />
 */
export function PaytypePickerField({
	payTypeId,       // paytype-specific prop name
	displayValue = "",
	onSelect,
	onClear,
	...rest             // label, isRequired, error, disabled, etc.
}) {
	return (
		<EntityPickerField
			{...rest}
			config={PAYTYPE_CONFIG}
			entityId={payTypeId}
			displayValue={displayValue}
			onSelect={onSelect}
			onClear={onClear}
		/>
	);
}

// ── PaytypePickerModal wrapper ────────────────────────────────────────────
/**
 * PaytypePickerModal
 * 
 * Usage (standalone):
 * <PaytypePickerModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   setData={(paytype) => {
 *     set("payTypeId", paytype.id);
 *     set("payTypeDescription", paytype.description);
 *   }}
 * />
 */
export function PaytypePickerModal({ isOpen, onClose, setData }) {
	return (
		<EntityPickerModal
			isOpen={isOpen}
			onClose={onClose}
			setData={setData}
			config={PAYTYPE_CONFIG}
		/>
	);
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { PaytypePickerField as default };